import AsyncStorage from '@react-native-async-storage/async-storage';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useCurrentOrgRole } from '../../../hooks/useAuthOrgRole';
import { isOrgLeadDetailQuery } from '../../../hooks/useLeads';
import { onboardingApi } from '../api/onboardingApi';
import { markOnboardingApplied } from '../appliedOrgs';
import { canReadOnboardingState } from '../roles';
import type {
  ApplyPlanInput,
  CreateOrganizationProductInput,
  DeleteOrganizationProductInput,
  ImportOrganizationProductsInput,
  OnboardingMode,
  PatchStateInput,
  ProductTemplateLocale,
  SaveProfileInput,
  UpdateOrganizationProductInput,
} from '../types';

const PRODUCT_WRITE_META = { skipGlobalErrorToast: true } as const;

export const ONBOARDING_KEYS = {
  all: ['onboarding'] as const,
  /**
   * The served plan is rendered into one language, so the cache entry is per
   * language. Call WITHOUT a language to get the prefix that matches every
   * variant — what invalidateQueries needs. setQueryData does NOT prefix-match,
   * so a writer must pass the language or its write lands on a key no reader
   * ever looks at.
   */
  state: (organizationId: string, language?: string) =>
    (language
      ? [...ONBOARDING_KEYS.all, 'state', organizationId, language]
      : [...ONBOARDING_KEYS.all, 'state', organizationId]) as readonly unknown[],
  profile: (organizationId: string, language?: string) =>
    (language
      ? [...ONBOARDING_KEYS.all, 'profile', organizationId, language]
      : [...ONBOARDING_KEYS.all, 'profile', organizationId]) as readonly unknown[],
  products: (organizationId: string) =>
    [...ONBOARDING_KEYS.all, 'products', organizationId] as const,
  productLinkedLeads: (organizationId: string, productId: string) =>
    [...ONBOARDING_KEYS.all, 'product-linked-leads', organizationId, productId] as const,
};

// The served plan is rendered into `language`, so it belongs in the query key:
// without it a language switch would keep serving the previously-rendered plan
// from cache and the wizard would rewind to regenerate.
export function useOnboardingState(organizationId: string) {
  const { language } = useTranslation();

  return useQuery({
    queryKey: ONBOARDING_KEYS.state(organizationId, language),
    queryFn: () => onboardingApi.getState(organizationId, language),
    enabled: !!organizationId,
    // This answer decides a redirect in app/(app)/_layout.tsx, so it must not be
    // served from a stale cache. The global defaults are tuned for content
    // (staleTime 5min, no refetch on focus) AND the cache is persisted to
    // AsyncStorage for 24h, so one wrong entry keeps bouncing the user back into
    // onboarding across reloads and restarts. The layout mounts once per app
    // session, so revalidating costs a single request.
    staleTime: 0,
  });
}

// Read-only org "general context" consumed by the Agents tab. Shared company
// context that guides agents without their own customization card.
/** Local record that this device hid the checklist. */
export const ONBOARDING_DISMISSED_KEY = 'onboarding_dismissed';

/**
 * Whether the onboarding checklist should stay hidden.
 *
 * The dismissal used to live only in this device's AsyncStorage, so an org that
 * had already waved the checklist away saw it again on the next browser. The
 * server has recorded `dismissedAt` since the dismiss action started reaching
 * it; this reads that back, so either source hiding it is enough.
 *
 * Only owner/admin may read that server flag. Everyone else falls back to the
 * local one, which is what this hook did on its own before the server had
 * anything to say.
 */
export function useOnboardingDismissed(organizationId: string | undefined) {
  const [dismissedHere, setDismissedHere] = useState(false);
  const role = useCurrentOrgRole();

  // Disabled via an empty id — the mechanism app/(app)/_layout.tsx already uses
  // to keep this query idle (no request, no 403, no Sentry capture) for the
  // roles the endpoint refuses.
  const { data } = useOnboardingState(
    canReadOnboardingState(role) && organizationId ? organizationId : '',
  );

  useEffect(() => {
    AsyncStorage.getItem(ONBOARDING_DISMISSED_KEY).then((value) => {
      if (value === 'true') setDismissedHere(true);
    });
  }, []);

  // Hide on the tap, not on the round trip: the write is persisted behind it.
  const markDismissed = useCallback(() => {
    setDismissedHere(true);
    void AsyncStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
  }, []);

  return { dismissed: dismissedHere || Boolean(data?.dismissedAt), markDismissed };
}

export function useOnboardingProfile(organizationId: string) {
  const { language } = useTranslation();

  return useQuery({
    queryKey: ONBOARDING_KEYS.profile(organizationId, language),
    queryFn: () => onboardingApi.getProfile(organizationId, language),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useOrganizationProducts(organizationId: string) {
  return useQuery({
    queryKey: ONBOARDING_KEYS.products(organizationId),
    queryFn: () => onboardingApi.listProducts(organizationId),
    enabled: !!organizationId,
  });
}

export function useProductLinkedLeads(organizationId: string, productId: string | null) {
  return useQuery({
    queryKey: ONBOARDING_KEYS.productLinkedLeads(organizationId, productId ?? ''),
    queryFn: () => onboardingApi.getProductLinkedLeads(organizationId, productId as string),
    enabled: !!organizationId && !!productId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Product mutations invalidate the list rather than writing a single row into
 * the cache: add/remove/reorder change the whole collection (positions shift),
 * and the server is the only thing that knows the resulting order.
 */
function useRefreshProductListAndOrgLeadDetails() {
  const queryClient = useQueryClient();

  return (organizationId: string) => {
    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) => isOrgLeadDetailQuery(queryKey, organizationId),
    });
    return queryClient.invalidateQueries({ queryKey: ONBOARDING_KEYS.products(organizationId) });
  };
}

export function useCreateOrganizationProduct() {
  const refreshProductsAndLeadDetails = useRefreshProductListAndOrgLeadDetails();

  return useMutation({
    mutationFn: (input: CreateOrganizationProductInput) => onboardingApi.createProduct(input),
    meta: PRODUCT_WRITE_META,
    onSuccess: (_data, variables) => {
      void refreshProductsAndLeadDetails(variables.organizationId);
    },
  });
}

export function useUpdateOrganizationProduct() {
  const refreshProductsAndLeadDetails = useRefreshProductListAndOrgLeadDetails();

  return useMutation({
    mutationFn: (input: UpdateOrganizationProductInput) => onboardingApi.updateProduct(input),
    meta: PRODUCT_WRITE_META,
    onSuccess: (_data, variables) => {
      void refreshProductsAndLeadDetails(variables.organizationId);
    },
  });
}

export function useDeleteOrganizationProduct() {
  const refreshProductsAndLeadDetails = useRefreshProductListAndOrgLeadDetails();

  return useMutation({
    mutationFn: (input: DeleteOrganizationProductInput) => onboardingApi.deleteProduct(input),
    meta: PRODUCT_WRITE_META,
    onSuccess: (_data, variables) => {
      void refreshProductsAndLeadDetails(variables.organizationId);
    },
  });
}

export function useImportOrganizationProducts() {
  const refreshProductsAndLeadDetails = useRefreshProductListAndOrgLeadDetails();

  return useMutation({
    mutationFn: (input: ImportOrganizationProductsInput) => onboardingApi.importProducts(input),
    onSuccess: (_data, variables) => {
      void refreshProductsAndLeadDetails(variables.organizationId);
    },
  });
}

export function useDownloadProductsTemplate() {
  return useMutation({
    mutationFn: (locale: ProductTemplateLocale) => onboardingApi.downloadProductsTemplate(locale),
  });
}

/**
 * Refreshes every cached language variant of an org's onboarding state.
 *
 * Mutations whose response is the raw OrgOnboardingState row cannot write it
 * into the cache directly: the row's plan is canonical English, and dropping it
 * into a Spanish entry would make the wizard treat the plan as stale and rewind
 * to regenerate. Invalidating makes them refetch through the read edge, which
 * renders the plan in the caller's language.
 */
function invalidateOnboardingState(queryClient: QueryClient, organizationId: string): void {
  void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEYS.state(organizationId) });
}

export function useSaveProfile() {
  const queryClient = useQueryClient();
  const { language } = useTranslation();

  return useMutation({
    mutationFn: (input: SaveProfileInput) =>
      onboardingApi.saveProfile({ ...input, outputLanguage: language }),
    onSuccess: (_data, variables) => {
      invalidateOnboardingState(queryClient, variables.organizationId);
      void queryClient.invalidateQueries({
        queryKey: ONBOARDING_KEYS.profile(variables.organizationId),
      });
    },
  });
}

export function useGeneratePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onboardingApi.generatePlan,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ONBOARDING_KEYS.state(variables.organizationId, variables.outputLanguage),
        data,
      );
      invalidateOnboardingState(queryClient, variables.organizationId);
    },
  });
}

export function useApplyPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ApplyPlanInput) => onboardingApi.applyPlan(input),
    // Written synchronously onto the exact key useOnboardingState reads, not
    // just invalidated: the success step's CTA navigates immediately, and the
    // layout guard re-runs before any refetch could land. A miss here sends the
    // user straight back into the wizard they just finished.
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ONBOARDING_KEYS.state(variables.organizationId, variables.outputLanguage),
        data,
      );
      // Apply committed, so onboarding IS finished — the gate must not have to
      // re-derive that from a cache read. See appliedOrgs.ts.
      markOnboardingApplied(variables.organizationId);
      invalidateOnboardingState(queryClient, variables.organizationId);
    },
  });
}

export function usePatchState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PatchStateInput) => onboardingApi.patchState(input),
    onSuccess: (_data, variables) => {
      invalidateOnboardingState(queryClient, variables.organizationId);
    },
  });
}

/**
 * Records that the org walked away from onboarding.
 *
 * The checklist used to be dismissed with a local AsyncStorage flag alone, so
 * the server never learned it happened: onboarding.dismissed never fired, and
 * the checklist reappeared on the next device. Abandonment is the counterpart
 * to onboarding.completed — without it an activation rate has no denominator
 * for the people who gave up.
 */
export function useDismissOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { organizationId: string }) =>
      onboardingApi.patchState({ organizationId: input.organizationId, action: 'dismiss' }),
    onSuccess: (_data, variables) => {
      invalidateOnboardingState(queryClient, variables.organizationId);
    },
  });
}

export function useSetOnboardingMode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { organizationId: string; onboardingMode: OnboardingMode }) =>
      onboardingApi.patchState({
        organizationId: input.organizationId,
        action: 'set-mode',
        onboardingMode: input.onboardingMode,
      }),
    onSuccess: (_data, variables) => {
      invalidateOnboardingState(queryClient, variables.organizationId);
    },
  });
}
